import API from '@/services/api';
import { MCD } from '@/services/app';
import { connect } from 'react-redux';
import { useDataLayer } from '@CS/hooks';
import { isDEV } from '@/utils/Location';
import FamilyInfo from '@/components/FamilyInfo';
import ProgressBar from '@/components/ProgressBar';
import type { Family } from '@/services/api/types';
import { mapAvatarResponse } from '@/utils/common';
import AvatarCreator from '@/components/AvatarCreator';
import { getUserFamily } from '@/redux/user/selectors';
import type { FamilyData } from '@/components/FamilyInfo';
import { useState, useRef, useMemo, useEffect } from 'react';
import { addPlexureTags /*, removePlexureTags */ } from '@/services/app/mcd';
import { ErrorPopup } from '@/components/Popups';
import { getCampaign } from '@/redux/campaign/selectors';
import type { CampaignState } from '@/redux/campaign/types';

type EditorProps = {
  onLoad: () => void;
  family?: Family;
  campaign: CampaignState;
};

const Editor = ({ family, onLoad, campaign }: EditorProps) => {
  const getAvatarById = () => family?.avatars.find(({ id }) => id === avatarId);

  const [familyData, setFamilyData] = useState<FamilyData | null>(null);
  const [visibleFamilyInfo, setVisibleFamilyInfo] = useState(false);
  const [serverNameError, setServerNameError] = useState<string | null>(null);

  const avatarId = new URLSearchParams(location.search).get('id');
  const [editFamilyData, setEditFamilyData] = useState(false);

  const toggleFullscreen = () => {
    if (isDEV()) return;
    MCD.toggleFullscreen();
    MCD.orientationScreen();
  };

  const onTabUpdate = (index: number) => {
    !index && toggleFullscreen();
    setTabIndex(index);
  };

  useDataLayer({
    event: 'page_view',
    eventProperties: {
      page_virtual_path: '/fam-mode-2/creating-avatars/customization',
      page_type: 'fam-mode-2'
    }
  });

  const onBack = () => setVisibleFamilyInfo(true);
  const [tabIndex, setTabIndex] = useState(0);
  const baseModelUrl = useRef<string>('');

  const updateFamily = async (data: FamilyData) => {
    console.log('Family Update:', data);

    try {
      setFamilyData(data);
      await API.updateFamily(data.name);

      // const members = family?.avatars.length ?? 1;
      // let id = Math.min(53 + members, 60);

      // await removePlexureTags([`NL_Custom_${id}`]);
      // id = Math.min(53 + data.members, 60);

      // await addPlexureTags([`NL_Custom_${id}`]);
      setVisibleFamilyInfo(false);
    } catch (error) {
      console.error(error);
    }
  };

  const handleNameError = (error: any) => {
    const { abusiveWordError } = campaign.translations;
    const message = error?.response?.data?.message || '';
    const isAbusiveWordError = message.toLowerCase().includes('abusive');

    if (isAbusiveWordError) {
      setServerNameError(String(abusiveWordError));
    } else {
      setServerNameError('There was an error, please try again later');
    }
  };
  const submitFamily = (data: FamilyData) => {
    setServerNameError(null);

    if (visibleFamilyInfo) {
      updateFamily(data)
        .catch((error) => {
          handleNameError(error);
        });
    } else {
      API.createFamily({ name: data.name })
        .then(({ baseModelUrl: url }) => {
          setFamilyData(data);
          setEditFamilyData(true);
          baseModelUrl.current = url;
          // const id = Math.min(53 + data.members, 60);
          addPlexureTags(['NL_Custom_53' /*, `NL_Custom_${id}` */]);
        })
        .catch((error) => {
          handleNameError(error);
        });
    }
  };

  const avatarDetails = useMemo(() => {
    baseModelUrl.current = family?.baseModelUrl ?? '';
    if (!avatarId) return;

    const avatar = getAvatarById();
    if (!avatar) return;

    baseModelUrl.current = avatar.baseModelUrl;
    return mapAvatarResponse(avatar);
  }, [avatarId]);

  useEffect(() => {
    (!family || !family.avatars.length) && onLoad();
  }, []);

  return (
    <>
      <ProgressBar familyData={editFamilyData} currentStep={tabIndex} />

      {visibleFamilyInfo || !family ? (
        <FamilyInfo onSubmit={submitFamily} data={family} />
      ) : (
        <AvatarCreator
          baseModelUrl={baseModelUrl.current}
          avatars={family.avatars.length}
          members={familyData?.members}
          onTabUpdate={onTabUpdate}
          canBack={editFamilyData}
          data={avatarDetails}
          onBack={onBack}
          id={avatarId}
        />
      )}

    {serverNameError && (
      <ErrorPopup
        error={serverNameError}
        onClose={() => setServerNameError(null)}
        onClick={() => setServerNameError(null)}
      />
    )}
    </>
  );
};

const mapStateToProps = (state: never) => ({
  family: getUserFamily(state),
  campaign: getCampaign(state)
});

export default connect(mapStateToProps)(Editor);
